/* eslint-disable react/no-unstable-nested-components */
import { zodResolver } from "@hookform/resolvers/zod";
import type { SignUpFormData } from "components/auth/form";
import { getSignUpFormDataShape } from "components/auth/form";
import { Button } from "components/ui/Button";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { Input } from "components/ui/Input";
import startCase from "lodash/startCase";
import { useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import CountryCombobox from "components/settings/CountryCombobox";
import { Turnstile } from "@marsidev/react-turnstile";

interface SignUpFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  country: string;
  turnstileToken: string;
  turnstileIdempotencyKey: string;
}

interface SignUpFormProps {
  onSubmit: (values: SignUpFormValues) => any | Promise<any>;
}

export default function SignUpForm({ onSubmit }: SignUpFormProps): ReactElement {
  const intl = useIntl();
  const [turnstileIdempotencyKey] = useState(() => crypto.randomUUID());

  const form = useForm<SignUpFormData>({
    mode: "onSubmit",
    resolver: zodResolver(getSignUpFormDataShape(intl)),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      country: "",
      turnstileToken: "",
    },
  });

  const handleSubmit = (values: SignUpFormData) => {
    return onSubmit({
      ...values,
      firstName: startCase(values.firstName.trim()),
      lastName: startCase(values.lastName.trim()),
      turnstileIdempotencyKey,
    });
  };

  const {
    formState: { isSubmitting },
  } = form;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off" className="grid gap-y-5 text-left">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Nome",
                    id: "CH/6DN",
                  })}
                  name="firstName"
                  maxLength={36}
                  className="capitalize"
                />

                <FormMessage />
              </div>
            );
          }}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Sobrenome",
                    id: "lLV5Ne",
                  })}
                  name="lastName"
                  maxLength={36}
                  className="capitalize"
                />

                <FormMessage />
              </div>
            );
          }}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="email"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="Email"
                  name="email"
                  maxLength={256}
                />

                <FormMessage />
              </div>
            );
          }}
        />

        {isHotmailOrOutlook(form.getValues("email") || "") && (
          <div className="-mx-6 bg-blue-50 px-6 py-3">
            <p className="m-0 text-center text-sm font-medium tracking-wide text-blue-600">
              <FormattedMessage
                defaultMessage={`<strong>Atenção!</strong> Este servidor de e-mail costuma ter problemas para receber a confirmação de cadastro, recomendamos que utilize um Gmail e clique em "Logar com o Google" para acesso imediato.`}
                id="1MH86u"
                description="Sign up form warning message for hotmail and outlook users"
                values={{
                  strong: (text) => <strong>{text}</strong>,
                }}
              />
            </p>
          </div>
        )}

        {isGmail(form.getValues("email") || "") && (
          <div className="-mx-6 max-w-sm text-pretty bg-blue-50 px-6 py-3 text-sm font-medium text-blue-600">
            <FormattedMessage
              defaultMessage="<strong>Atenção!</strong> Como você possui um Gmail, cadastre-se com a conta Google que não será necessário confirmar o e-mail e nem criar uma senha de acesso."
              id="+ZerjW"
              description="Sign up form warning message for Gmail users"
              values={{
                strong: (text) => <strong>{text}</strong>,
              }}
            />
          </div>
        )}

        <FormField
          control={form.control}
          name="country"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <CountryCombobox country={field.value} withoutLabel={true} onCountryChange={field.onChange} />

                <FormMessage />
              </div>
            );
          }}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="password"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Senha",
                    id: "yUZJVn",
                  })}
                  name="password"
                  maxLength={256}
                />

                <FormMessage />
              </div>
            );
          }}
        />

        <Button type="submit" variant="default" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
          ) : (
            <FormattedMessage defaultMessage="Quero me cadastrar" description="Sign up page" id="/czduW" />
          )}
        </Button>

        <FormField
          control={form.control}
          name="turnstileToken"
          render={({ field }) => {
            return (
              <Turnstile
                siteKey={process.env.TURNSTILE_SITE_KEY || ""}
                className="mx-auto"
                onSuccess={(token) => {
                  field.onChange(token);
                }}
                options={{
                  theme: "auto",
                  language: "auto",
                  appearance: "always",
                  size: "flexible",
                  feedbackEnabled: false,
                  responseField: false,
                }}
              />
            );
          }}
        />
      </form>
    </Form>
  );
}

function isHotmailOrOutlook(email: string) {
  const lowerEmail = email.toLowerCase();

  return ["hotmail.com", "hotmail.com.br", "outlook.com", "outlook.com.br"].some((domain) => {
    return lowerEmail.endsWith(domain);
  });
}
function isGmail(email: string) {
  const lowerEmail = email.toLowerCase();
  return lowerEmail.endsWith("gmail.com");
}
